import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Location} from '@angular/common';
import {Title} from '@angular/platform-browser';
import {DataService} from '../../../../Services/data.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {EmailService} from '../../../../Services/email.service';
import {EmailModel} from '../../../../DataModels/email-model';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {DateFilterService} from '../../../../Services/dateFilter.service';
import {Modal} from 'bootstrap';
import {lastValueFrom} from 'rxjs';

@Component({
  selector: 'app-check-register',
  templateUrl: './check-register.component.html',
  styleUrls: ['./check-register.component.css'],
  standalone: false,
})
export class CheckRegisterComponent implements OnInit {
  @ViewChild('PrintRoot', {static: false}) PrintRoot: ElementRef;

  vm: any = {
    report: [],
    allReport: [],
    expandAll: false,
    request: {
      fromDate: null,
      toDate: null,
      company_id: null,
      bank_id: null,
      payment_type_id: null,
      date_field: 'header_date',
      issued_only: true,
      include_voided: true,
    },
    fromDateSelect: null,
    toDateSelect: null,
    showSpecificDates: false,
    company: [],
    bank: [],
    paymentTypes: [],
    buttonsDisabled: false,
  };

  buttonConfig: any = {
    reportFormat: false,
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    filters: false,
    clear: false,
    disableOnEmpty: true,
  };

  reportDataToSendInEmail: EmailModel = new EmailModel();
  loading = false;
  errorMessage = '';

  constructor(
    private location: Location,
    private titleService: Title,
    private tableDataService: DataService,
    private notification: BsToastService,
    private emailService: EmailService,
    private reportPrint: ReportPrintService,
    private dateFilter: DateFilterService,
  ) {
    this.titleService.setTitle('Check Register');
  }

  ngOnInit(): void {
    const init = this.dateFilter.DateFilterInit(new Date().toISOString(), new Date().toISOString());
    this.vm.fromDateSelect = init.fromDateSelect;
    this.vm.toDateSelect = init.toDateSelect;

    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const currentMonthRange = this.dateFilter.GetDefaultCurrentMonthRange(this.vm.fromDateSelect, this.vm.toDateSelect, today);
    const fromMonthRange = this.dateFilter.GetDefaultCurrentMonthRange(this.vm.fromDateSelect, this.vm.toDateSelect, ninetyDaysAgo);

    this.vm.request.fromDate = fromMonthRange.fromDate;
    this.vm.request.toDate = currentMonthRange.toDate;

    this.loadFilterOptions();
  }

  private async loadFilterOptions(): Promise<void> {
    try {
      const [companyResp, bankResp, ptResp] = await Promise.all([
        lastValueFrom(this.tableDataService.getApiListData('company', '?state=active&pageNumber=0&pageSize=999999')),
        lastValueFrom(this.tableDataService.getApiListData('bank', '?state=active&pageNumber=0&pageSize=999999')),
        lastValueFrom(this.tableDataService.getApiListData('payment_type', '?state=active&pageNumber=0&pageSize=999999')),
      ]);
      this.vm.company = (companyResp as any)?.Data || [];
      this.vm.bank = (bankResp as any)?.Data || [];
      this.vm.paymentTypes = (ptResp as any)?.Data || [];
    } catch {
      this.notification.showErrorToast('Failed to load filter options.');
    }
  }

  private normalizeDate(value: any): string | undefined {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'object' && Number.isFinite(value.year)) {
      return `${value.year}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`;
    }
    const raw = String(value || '').trim();
    if (!raw) {
      return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return raw;
    }
    const dt = new Date(raw);
    return !Number.isNaN(dt.getTime()) ? dt.toISOString().slice(0, 10) : undefined;
  }

  async load(): Promise<void> {
    const fromDate = this.normalizeDate(this.vm.request.fromDate ?? this.vm.fromDateSelect);
    const toDate = this.normalizeDate(this.vm.request.toDate ?? this.vm.toDateSelect);

    if (!this.vm.request.company_id) {
      this.notification.showWarningToast('Company is required.');
      return;
    }
    if (!fromDate || !toDate) {
      this.notification.showWarningToast('From Date and To Date are required.');
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.vm.report = [];

    try {
      let params = `?fromDate=${fromDate}&toDate=${toDate}&company_id=${this.vm.request.company_id}`;
      if (this.vm.request.bank_id) {
        params += `&bank_id=${this.vm.request.bank_id}`;
      }
      if (this.vm.request.payment_type_id) {
        params += `&payment_type_id=${this.vm.request.payment_type_id}`;
      }
      params += `&date_field=${this.vm.request.date_field || 'header_date'}`;
      params += `&issued_only=false`;
      params += `&issuedOnly=false`;
      params += `&include_voided=true`;
      params += `&includeVoided=true`;
      const result = await lastValueFrom(this.tableDataService.getApiListData('CheckRegisterReport', params));
      this.vm.allReport = (result as any)?.Data || (result as any) || [];
      this.applyFilters();
    } catch (err: any) {
      const msg = err?.error?.Message || err?.error?.message || err?.message || 'Failed to load Check Register.';
      this.errorMessage = msg;
      this.notification.showErrorToast(msg);
    } finally {
      this.loading = false;
    }
  }

  update(): void {
    this.load();
  }

  toggleExpandAll(): void {
    const expand = this.vm.expandAll;
    (this.vm.report || []).forEach((r: any) => r.__expanded = expand);
  }

  applyFilters(): void {
    let all = this.vm.allReport || [];
    if (!this.vm.request.include_voided) {
      all = all.filter((r: any) => !r.is_voided);
    }
    if (this.vm.request.issued_only) {
      all = all.filter((r: any) => r.payment_issued || r.is_voided);
    }
    this.vm.report = all;
  }

  clear(): void {
    this.vm.report = [];
    this.vm.request.bank_id = null;
    this.vm.request.payment_type_id = null;
    this.vm.request.date_field = 'header_date';
    this.vm.request.issued_only = true;
    this.vm.request.include_voided = true;
    this.errorMessage = '';
  }

  close(): void {
    this.location.back();
  }

  toggleRow(row: any): void {
    row.__expanded = !row.__expanded;
  }

  formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const s = String(value);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      return `${m[2]}/${m[3]}/${m[1]}`;
    }
    const d = new Date(s);
    if (isNaN(d.getTime())) {
      return s;
    }
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  }

  formatMoney(value: any): string {
    const n = Number(value || 0);
    return n.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  get reportTotal(): number {
    return (this.vm.report || []).reduce((sum: number, r: any) => sum + Math.abs(Number(r.amount || 0)), 0);
  }

  printReport(): void {
    const html = this.buildPrintHtml();
    this.reportPrint.printInIframe(html);
  }

  setEmailData(): void {
    const fromDate = this.normalizeDate(this.vm.request.fromDate ?? this.vm.fromDateSelect) || '';
    const toDate = this.normalizeDate(this.vm.request.toDate ?? this.vm.toDateSelect) || '';
    const emailDocObj = {} as EmailModel;
    const reportName = 'Check Register';

    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '' as any;
    emailDocObj.toEmail = '';
    emailDocObj.body = `Please see the attached ${reportName}.<br><br><br>Best regards,<br><br>AcctXErp<br><br>`;
    emailDocObj.Subject = `AcctXErp ${reportName}` + (fromDate || toDate ? ` (${fromDate || '—'} to ${toDate || '—'})` : '');

    try {
      emailDocObj.pdfHtml = this.buildPrintHtml();
    } catch {
      emailDocObj.pdfHtml = '';
    }
    emailDocObj.activity_message = reportName;

    const now = new Date();
    emailDocObj.FileName = `Check_Register_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;

    this.reportDataToSendInEmail = emailDocObj;
    setTimeout(() => {
      const element = document.getElementById('sendEmailModal') as HTMLElement;
      if (element) {
        new Modal(element).show();
      }
    }, 0);
  }

  private buildPrintHtml(): string {
    const rawContent = (this.PrintRoot?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const from = this.normalizeDate(this.vm.request.fromDate) || '';
    const to = this.normalizeDate(this.vm.request.toDate) || '';
    const meta: { label: string; value: string }[] = [];
    if (from || to) {
      meta.push({label: 'Period', value: `${from || '—'} to ${to || '—'}`});
    }
    return this.reportPrint.buildHtml({title: 'Check Register', contentHtml: content, meta, orientation: 'landscape'});
  }
}
